import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Button,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';

import { useEntity } from '../../context/EntityContext';
import { getPublications, PublicationListResponse } from '../../services/publicationsService';

// Formatação de data/hora simples, sem "às"
function formatFullDateTime(dateString: string | null): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).format(date);
  } catch {
    return dateString;
  }
}

const AllPublications: React.FC = () => {
  const navigate = useNavigate();
  const { selectedEntity } = useEntity();
  const [data, setData] = useState<PublicationListResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(5); // Exemplo de 5 itens por página

  const fetchData = async (entityId: string, pageNum: number) => {
    try {
      setLoading(true);
      const result = await getPublications(entityId, pageNum, limit);
      setData(result);
    } catch (error) {
      console.error('Erro ao carregar publicações:', error);
      swal('Erro', 'Não foi possível carregar as publicações.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedEntity) {
      fetchData(selectedEntity, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity, page]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleRowClick = (pub: any) => {
    navigate(`/publicacoes/${pub.id}`);
  };

  const colorArray = ["primary","secondary","success","error","warning","info"] as const;

  if (!selectedEntity) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="body1" color="error">
          Selecione uma entidade antes de prosseguir.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Publicações</Typography>
        <Box>
          <Button variant="outlined" sx={{ mr: 2 }} onClick={() => navigate(-1)}>
            Voltar
          </Button>
          <Button variant="contained" onClick={() => navigate('/publicacoes/nova')}>
            Nova Publicação
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <>
          {data.items.length === 0 ? (
            <Typography>Nenhuma publicação encontrada.</Typography>
          ) : (
            <Paper sx={{ p: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Título</TableCell>
                    <TableCell>Publicado em</TableCell>
                    <TableCell>Resumo</TableCell>
                    <TableCell>Categorias</TableCell>
                    <TableCell>Onde?</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.items.map((pub) => {
                    const publishedDate = formatFullDateTime(pub.published_at);
                    // Se `_serialize_publication` retorna "categories" e "is_for_dom/dou/jgc":
                    const catItems = (pub as any).categories || [];
                    const isForDom = (pub as any).is_for_dom || false;
                    const isForDou = (pub as any).is_for_dou || false;
                    const isForJgc = (pub as any).is_for_jgc || false;

                    return (
                      <TableRow
                        key={pub.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleRowClick(pub)}
                      >
                        <TableCell>{pub.title}</TableCell>
                        <TableCell>{publishedDate}</TableCell>
                        <TableCell>{pub.summary}</TableCell>
                        <TableCell>
                          {catItems.length === 0 && <Typography variant="caption">—</Typography>}
                          {catItems.map((c: any, idx: number) => (
                            <Chip
                              key={c.id}
                              label={c.category}
                              size="small"
                              variant="outlined"
                              color={colorArray[idx % colorArray.length]}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </TableCell>
                        <TableCell>
                          {isForDom && (
                            <Chip label="Diário Oficial (Próprio)" color="info" variant="outlined" size="small" sx={{ mr: 0.5 }}/>
                          )}
                          {isForDou && (
                            <Chip label="Diário Oficial da União" color="warning" variant="outlined" size="small" sx={{ mr: 0.5 }}/>
                          )}
                          {isForJgc && (
                            <Chip label="Jornal de Grande Circulação" color="error" variant="outlined" size="small" sx={{ mr: 0.5 }}/>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              page={data.page}
              count={Math.ceil(data.total / data.size)}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default AllPublications;